import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import useCart from '../hooks/useCart';
import './OrderConfirmationPage.css';

const getPaymentMethodLabel = (paymentMethod = '') => {
  const normalizedMethod = String(paymentMethod || '').trim().toLowerCase();

  if (normalizedMethod === 'online' || normalizedMethod === 'upi' || normalizedMethod === 'razorpay') {
    return 'Online Payment';
  }

  return normalizedMethod ? 'Offline Payment' : 'Online Payment';
};

const normalizePaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    return null;
  }

  if (typeof paymentMethod === 'string') {
    return {
      id: paymentMethod,
      name: getPaymentMethodLabel(paymentMethod)
    };
  }

  const methodId = paymentMethod.id || paymentMethod.paymentMethod || '';

  return {
    ...paymentMethod,
    id: methodId,
    name: getPaymentMethodLabel(methodId)
  };
};

const isAwaitingOnlinePayment = (orderData) =>
  orderData?.orderStatus === 'Processing' || orderData?.orderStatus === 'Order Placed';

const OrderConfirmationPage = () => {
  const [order, setOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTitle, setLoadingTitle] = useState('Confirming Your Payment');
  const [loadingMessage, setLoadingMessage] = useState('Please wait while we verify your payment and prepare your order confirmation.');
  const [orderStatus, setOrderStatus] = useState('processing');
  const [estimatedDelivery, setEstimatedDelivery] = useState('');
  const [error, setError] = useState(''); // Add missing error state
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();

  const redirectToPaymentFailure = (paymentState = 'failed', message = '') => {
    navigate('/payment-failed', {
      replace: true,
      state: {
        paymentState,
        message: message || (
          paymentState === 'incomplete'
            ? 'Payment not completed. Please proceed again.'
            : 'Payment failed. Please try again.'
        )
      }
    });
  };

  const getPaymentFailureState = (orderData) => {
    const paymentState = orderData?.paymentResult?.failure_type === 'incomplete' ||
      orderData?.orderStatus === 'Payment Not Completed'
      ? 'incomplete'
      : 'failed';

    return {
      paymentState,
      message: orderData?.paymentResult?.failure_reason || (
        paymentState === 'incomplete'
          ? 'Payment not completed. Please proceed again.'
          : 'Payment failed. Please try again.'
      )
    };
  };

  useEffect(() => {
    let retryTimeout = null;
    let isMounted = true;

    const fetchOrderDetails = async () => {
      let keepLoadingForPaymentVerification = false;
      setLoading(true);
      try {
        // Check if we have order data from navigation state
        if (location.state?.order) {
          console.log('Order data found in location state:', location.state.order);
          const orderFromState = location.state.order;
          
          // Check if the order has failed payment status
          if (orderFromState.orderStatus === 'Payment Failed' ||
              orderFromState.orderStatus === 'Payment Not Completed' ||
              orderFromState.paymentResult?.status === 'FAILED' ||
              (orderFromState.paymentMethod === 'online' && !orderFromState.isPaid && !isAwaitingOnlinePayment(orderFromState))) {
            console.error('Order in location state indicates payment was not completed successfully');
            const failureState = getPaymentFailureState(orderFromState);
            redirectToPaymentFailure(failureState.paymentState, failureState.message);
            return;
          }
          
          setOrder(orderFromState);
          if (location.state.paymentMethod) {
            setPaymentMethod(normalizePaymentMethod(location.state.paymentMethod));
          } else {
            // Fallback to determining payment method from order data
            setPaymentMethod(normalizePaymentMethod(orderFromState.paymentMethod));
          }
        } else {
          // Get order data from sessionStorage
          const savedOrder = sessionStorage.getItem('lastOrder');
          const savedPaymentMethod = sessionStorage.getItem('lastPaymentMethod');
          
          if (savedOrder) {
            const parsedOrder = JSON.parse(savedOrder);
            
            // Check if the order has failed payment status
            if (parsedOrder.orderStatus === 'Payment Failed' ||
                parsedOrder.orderStatus === 'Payment Not Completed' ||
                parsedOrder.paymentResult?.status === 'FAILED' ||
                (parsedOrder.paymentMethod === 'online' && !parsedOrder.isPaid && !isAwaitingOnlinePayment(parsedOrder))) {
              console.error('Order in sessionStorage indicates payment was not completed successfully');
              sessionStorage.removeItem('lastOrder');
              sessionStorage.removeItem('lastPaymentMethod');
              const failureState = getPaymentFailureState(parsedOrder);
              redirectToPaymentFailure(failureState.paymentState, failureState.message);
              return;
            }
            
            setOrder(parsedOrder);
            
            if (savedPaymentMethod) {
              setPaymentMethod(normalizePaymentMethod(JSON.parse(savedPaymentMethod)));
            } else {
              // Fallback to determining payment method from order data
              setPaymentMethod(normalizePaymentMethod(parsedOrder.paymentMethod));
            }
          } else {
            // Check for payment failure indicators in URL
            const paymentStatus = searchParams.get('payment_status');
            const paymentFailed = searchParams.get('payment_failed');
            
            if (paymentStatus === 'failed' || paymentStatus === 'incomplete' || paymentFailed === 'true') {
              console.error('Payment failure detected in URL parameters');
              keepLoadingForPaymentVerification = true;
              setLoadingTitle('Checking Payment Status');
              setLoadingMessage('Please wait while we read the payment response from the gateway.');
              sessionStorage.removeItem('lastOrder');
              sessionStorage.removeItem('lastPaymentMethod');
              retryTimeout = setTimeout(() => {
                redirectToPaymentFailure(
                  paymentStatus === 'incomplete' ? 'incomplete' : 'failed',
                  paymentStatus === 'incomplete'
                    ? 'Payment not completed. Please proceed again.'
                    : 'Payment failed. Please try again.'
                );
              }, 900);
              return;
            }
            
            // If we have a local order ID in the URL, fetch the order details
            if (searchParams.get('id')) {
              console.log('Fetching order details for ID:', searchParams.get('id'));
              try {
                const { data } = await axios.post(
                  `${API_BASE}/v1/order/${searchParams.get('id')}`,
                  { withCredentials: true }
                );
                
                if (data.success) {
                  setOrder(data.order);
                  setPaymentMethod(normalizePaymentMethod(data.order.paymentMethod));
                } else {
                  setError('Failed to fetch order details');
                }
              } catch (fetchError) {
                console.error('Error fetching order details:', fetchError);
                setError('Failed to fetch order details');
              }
            } else {
              console.log('No order data found in state, sessionStorage, or query params');
              // No order data available, redirect to home after a delay
              setTimeout(() => {
                navigate('/');
              }, 5000);
            }
          }
        }

        // Clear the cart regardless of how we got the order data (COD or online payment)
        clearCart();

        // Set estimated delivery date (5-7 days from now)
        const deliveryDate = new Date();
        deliveryDate.setDate(deliveryDate.getDate() + 5 + Math.floor(Math.random() * 3));
        
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        setEstimatedDelivery(deliveryDate.toLocaleDateString('en-US', options));
        
        // Simulate order processing
        const timer = setTimeout(() => {
          setOrderStatus('confirmed');
        }, 3000);
        
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Error in order details processing:', error);
        setError('An error occurred while processing your order details');
      } finally {
        if (!keepLoadingForPaymentVerification && isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchOrderDetails();
    return () => {
      isMounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [location, navigate, searchParams]);

  // Ensure orderStatus is set to 'confirmed' when order is loaded
  useEffect(() => {
    if (order && orderStatus !== 'confirmed') {
      const timer = setTimeout(() => {
        setOrderStatus('confirmed');
      }, 1000); // 1 second delay for smooth transition
      return () => clearTimeout(timer);
    }
  }, [order]);

  // Clear lastOrder and lastPaymentMethod from sessionStorage after displaying the order
  useEffect(() => {
    if (order) {
      sessionStorage.removeItem('lastOrder');
      sessionStorage.removeItem('lastPaymentMethod');
    }
  }, [order]);

  const handleInvoiceDownload = async () => {
    try {
      if (!order) throw new Error('Order data not available');

      const response = await axios.get(`${API_BASE}/${order._id}/invoice/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `panchali-gst-invoice-${order._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to download invoice: ' + (err.message || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="processing-order processing-order--payment">
            <div className="confirmation-loader" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <h2>{loadingTitle}</h2>
            <p>{loadingMessage}</p>
            <small>We will automatically show your order confirmation or the payment failed page.</small>
          </div>
        </div>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="error-message">
            <h2>Order Information Not Available</h2>
            <p>We couldn't find your order information. Please check your orders in your account.</p>
            <p>If payment was deducted, please contact support with your payment reference.</p>
            {error && <p className="error-text">{error}</p>} {/* Display error message */}
            <div className="order-actions">
              <Link to="/account" className="account-link">Go to My Account</Link>
              <Link to="/" className="continue-shopping">Return to Home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="order-confirmation-page">
      <div className="confirmation-container">
        {orderStatus === 'processing' ? (
          <div className="processing-order">
            <div className="loader"></div>
            <h2>Processing Your Order</h2>
            <p>Please wait while we confirm your order...</p>
          </div>
        ) : (
          <>
            <div className="order-success">
              <div className="success-icon">✓</div>
              <h1>Thank You for Your Order!</h1>
              <p className="success-message">Your order has been placed successfully and is being processed.</p>
              <div className="order-number">
                <span>Order Number:</span> #{order._id}
              </div>
              <p className="confirmation-email">A confirmation email has been sent to your registered email address.</p>
            </div>
            
            <div className="order-details-card">
              <div className="card-header">
                <h2>Order Details</h2>
                <button onClick={handleInvoiceDownload} className="download-invoice-btn">
                  <span className="invoice-icon">📄</span> Download Invoice
                </button>
              </div>
              
              <div className="order-info-grid">
                <div className="order-info-column">
                  <div className="info-group">
                    <h3>Shipping Address</h3>
                    <p>{order.shippingAddress.fullName}</p>
                    <p>{order.shippingAddress.addressLine1}</p>
                    {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                    <p>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}</p>
                    <p>Phone: {order.shippingAddress.phone}</p>
                  </div>
                  
                  <div className="info-group">
                    <h3>Payment Information</h3>
                    <p><strong>Method:</strong> {paymentMethod ? paymentMethod.name : 'Online Payment'}</p>
                    <p><strong>Status:</strong> {!order.isPaid ? (
                      <span className="payment-status pending">Pending</span>
                    ) : (
                      <span className="payment-status paid">Paid</span>
                    )}</p>
                    <p><strong>Date:</strong> {new Date(order.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}</p>
                  </div>
                </div>
                
                <div className="order-info-column">
                  <div className="info-group">
                    <h3>Order Summary</h3>
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <span>₹{order.itemsPrice.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Shipping:</span>
                      <span>FREE</span>
                    </div>
                    {order.taxPrice > 0 && (
                      <div className="summary-row">
                        <span>Tax:</span>
                        <span>₹{order.taxPrice.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Total:</span>
                      <span>₹{order.totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="info-group">
                    <h3>Delivery Information</h3>
                    <p><strong>Estimated Delivery:</strong> {estimatedDelivery}</p>
                    <p><strong>Status:</strong> <span className="order-status processing">Processing</span></p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-items-card">
              <h2>Order Items</h2>
              <div className="order-items-list">
                {order.orderItems && order.orderItems.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-image">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="item-details">
                      <h4>{item.title}</h4>
                      <div className="item-meta">
                        <span className="item-quantity">Qty: {item.quantity}</span>
                        {item.selectedSize ? <span className="item-size">Size: {item.selectedSize}</span> : null}
                        <span className="item-price">₹{item.price.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="item-total">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-actions-card">
              <Link to="/" className="action-button home">
                <span className="action-icon">🏠</span> Continue Shopping
              </Link>
              <Link to={`/order/${order._id}`} className="action-button track">
                <span className="action-icon">📦</span> Track Order
              </Link>
              <Link to="/account" className="action-button account">
                <span className="action-icon">👤</span> My Account
              </Link>
            </div>
            
            <div className="customer-support">
              <h3>Need Help?</h3>
              <p>If you have any questions about your order, please contact our customer support.</p>
              <a href="mailto:support@panchalijewels.com" className="support-link">support@panchalijewels.com</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
